import { Box } from '@mui/material';
import {
  Children,
  cloneElement,
  isValidElement,
  type Attributes,
  type ReactElement,
  type ReactNode,
} from 'react';

import { useIsSmallScreen } from '../../hooks/useScreenSize';
import { findChildIndexByProp } from '../../utils/children';
import type { BaseComponentProps } from '../../utils/types';
import type { ButtonProps } from '../buttons/Button';
import { Columns } from './Columns';

interface BaseButtonRowProps extends BaseComponentProps {
  fullWidth?: boolean;
  responsive?: boolean;
  noPadding?: boolean;
  children?: ReactNode;
}

interface PrimaryButtonRowPropsBasedProps extends BaseButtonRowProps {
  primary: ReactElement<ButtonProps>;
  cancel?: ReactElement<ButtonProps>;
  noPrimary?: never;
  noCancel?: never;
}

interface BooleanPropsButtonRowProps extends BaseButtonRowProps {
  noPrimary: boolean;
  noCancel?: boolean;
  primary?: never;
  cancel?: never;
}

interface DelegatedButtonRowProps extends BaseButtonRowProps {
  primary?: never;
  cancel?: never;
  noPrimary?: never;
  noCancel?: never;
}

export type ButtonRowProps =
  | PrimaryButtonRowPropsBasedProps
  | BooleanPropsButtonRowProps
  | DelegatedButtonRowProps;

export const ButtonRow = ({
  children,
  primary,
  cancel,
  noPrimary = false,
  noCancel = false,
  fullWidth = false,
  responsive = true,
  noPadding = false,
}: ButtonRowProps) => {
  const isSmallScreen = useIsSmallScreen();
  const wideButtons = fullWidth || (responsive && isSmallScreen);

  if (!children && !primary && !cancel) return null;

  const childEls = Children.toArray(children);

  let primaryEl: ReactNode = primary;
  if (!primaryEl && !noPrimary) {
    const primaryIdx = findChildIndexByProp(children, 'isPrimary', true);
    if (primaryIdx > -1) {
      primaryEl = childEls.splice(primaryIdx, 1);
    }
  }

  let cancelEl: ReactNode = cancel;
  if (!cancelEl && !noCancel) {
    const cancelIdx = findChildIndexByProp(children, 'isCancel', true);
    if (cancelIdx > -1) {
      cancelEl = childEls.splice(cancelIdx, 1);
    }
  }

  const secondaryEls: ReactNode[] = [];
  childEls.forEach(el => {
    // Cover up for not marking which button was primary/cancel.
    // Assume the first element in the children is Primary, the second is Cancel
    if (!primaryEl && !noPrimary) {
      primaryEl = el;
      return;
    }
    // We'll only reach this block once we've already snagged a primary element as necessary
    // If we haven't found a secondary element yet, treat this button as secondary;
    if (!cancelEl && !noCancel) {
      cancelEl = el;
      return;
    }
    // Now that we have a primary and cancel as needed, all the rest are secondary
    secondaryEls.push(el);
  });

  const sxContainer = {
    display: 'flex',
    flex: wideButtons ? 1 : undefined,
    gap: 1,
    '& > *': wideButtons
      ? {
          flex: 1,
        }
      : undefined,
  };

  return (
    <Columns
      paddingTop={noPadding ? 0 : 1}
      spacing={1}
      justifyContent={wideButtons ? 'stretch' : 'space-between'}
    >
      {(primaryEl || cancelEl) && (
        <Box
          sx={{
            ...sxContainer,
            flexDirection: 'row',
            marginLeft: wideButtons ? undefined : 'auto',
            order: 99,
          }}
        >
          {cancelEl &&
            (isValidElement(cancelEl)
              ? cloneElement(cancelEl, {
                  isPrimary: false,
                  isCancel: true,
                  fullWidth: wideButtons,
                } as Attributes)
              : cancelEl)}
          {primaryEl &&
            (isValidElement(primaryEl)
              ? cloneElement(primaryEl, {
                  isPrimary: true,
                  isCancel: false,
                  fullWidth: wideButtons,
                } as Attributes)
              : primaryEl)}
        </Box>
      )}

      {secondaryEls.length > 0 && (
        <Box
          sx={{
            ...sxContainer,
            flexDirection: 'row',
          }}
        >
          {secondaryEls.map(el =>
            isValidElement(el)
              ? cloneElement(el, {
                  isPrimary: false,
                  isCancel: false,
                  fullWidth: wideButtons,
                } as Attributes)
              : el,
          )}
        </Box>
      )}
    </Columns>
  );
};
